export type RouteKind =
  | "diagnostic"
  | "forecast"
  | "joybuy-inventory"
  | "joybuy-operation"
  | "joybuy-traffic"
  | "joybuy-data-upload"
  | "amazon-inventory"
  | "global-sales"
  | "customer-target-attainment"
  | "invoice-reconciliation"
  | "amazon-promotion"
  | "global-profitability"
  | "data-sources"
  | "amazon-operation"
  | "secondary-sales"
  | "data-upload"
  | "amazon-data-upload"
  | "master-data"
  | "users-access"
  | "scm-global-inventory"
  | "cost"
  | "rebate-settlement"
  | "joybuy-order-planning"
  | "channel-sales"
  | "action-center";

export interface ShellRoute {
  path: string;
  label: string;
  title: string;
  group: "Global" | "Amazon" | "Joybuy" | "System";
  kind: RouteKind;
}

export interface NavigationGroup {
  label: ShellRoute["group"];
  routes: readonly ShellRoute[];
}

export type MinimumRouteRole = "VIEWER" | "ADMIN";

const globalRoutes = [
  ["/global/action-center", "Action Center", "Action Center"],
  ["/global/sales", "Global Sales", "Global Sales"],
  ["/global/inventory", "Planning & Inventory", "Planning & Inventory"],
  ["/global/cost", "Cost", "Global Cost"],
  ["/global/profitability", "Profitability", "Global Profitability"],
  [
    "/finance/invoice-reconciliation",
    "Invoice Reconciliation",
    "Invoice Reconciliation",
  ],
] as const;

const amazonRoutes = [
  ["/amazon/sales", "Sales", "Amazon Sales"],
  ["/amazon/inventory", "Inventory", "Amazon Inventory"],
  ["/amazon/promotion", "Promotion", "Amazon Promotion"],
  [
    "/amazon/rebate-settlement",
    "Rebate & Settlement",
    "Amazon Rebate & Settlement",
  ],
  ["/amazon/operations", "Operation", "Amazon Operation"],
  ["/amazon/data-upload", "Data Upload", "Amazon Data Upload"],
] as const;

const joybuyRoutes = [
  ["/joybuy/sales", "Sales", "Joybuy Sales"],
  ["/joybuy/inventory", "Inventory", "Joybuy Inventory"],
  ["/joybuy/order-planning", "Order Planning", "Joybuy Order Planning"],
  ["/joybuy/operation", "Operation", "Joybuy Operation"],
  ["/joybuy/traffic", "Traffic", "Joybuy Traffic"],
  ["/joybuy/data-upload", "Data Upload Center", "Joybuy Data Upload Center"],
] as const;

const systemRoutes = [
  ["/system/sell-out", "Sell-Out", "Secondary Sales / Sell-Out"],
  ["/system/data-import-center", "Data Upload", "Data Import Center"],
  ["/system/data-sources", "Data Sources", "Data Sources & Identity"],
  ["/system/master-data", "Master Data", "Master Data"],
  ["/system/users-access", "Users & Access", "Users & Access"],
] as const;

const migratedKinds = new Map<string, RouteKind>([
  ["/global/action-center", "action-center"],
  ["/global/sales", "global-sales"],
  ["/sales/customer-target-attainment", "customer-target-attainment"],
  ["/sales/forecast", "forecast"],
  ["/joybuy/inventory", "joybuy-inventory"],
  ["/joybuy/operation", "joybuy-operation"],
  ["/joybuy/traffic", "joybuy-traffic"],
  ["/joybuy/data-upload", "joybuy-data-upload"],
  ["/amazon/inventory", "amazon-inventory"],
  ["/finance/invoice-reconciliation", "invoice-reconciliation"],
  ["/amazon/promotion", "amazon-promotion"],
  ["/global/profitability", "global-profitability"],
  ["/system/data-sources", "data-sources"],
  ["/system/sell-out", "secondary-sales"],
  ["/amazon/operations", "amazon-operation"],
  ["/amazon/data-upload", "amazon-data-upload"],
  ["/system/data-import-center", "data-upload"],
  ["/system/master-data", "master-data"],
  ["/system/users-access", "users-access"],
  ["/global/inventory", "scm-global-inventory"],
  ["/global/cost", "cost"],
  ["/amazon/rebate-settlement", "rebate-settlement"],
  ["/joybuy/order-planning", "joybuy-order-planning"],
  ["/amazon/sales", "channel-sales"],
  ["/joybuy/sales", "channel-sales"],
]);

function businessRoutes(
  group: ShellRoute["group"],
  definitions: ReadonlyArray<readonly [string, string, string]>,
): ShellRoute[] {
  return definitions.map(([path, label, title]) => {
    const kind = migratedKinds.get(path);
    if (!kind) throw new Error(`Normal business route ${path} has no React capability`);
    return { path, label, title, group, kind };
  });
}

export const navigationGroups: readonly NavigationGroup[] = [
  { label: "Global", routes: businessRoutes("Global", globalRoutes) },
  { label: "Amazon", routes: businessRoutes("Amazon", amazonRoutes) },
  { label: "Joybuy", routes: businessRoutes("Joybuy", joybuyRoutes) },
  {
    label: "System",
    routes: [
      ...businessRoutes("System", systemRoutes),
      {
        path: "/diagnostics/backend-connectivity",
        label: "Backend Connectivity",
        title: "Backend Connectivity",
        group: "System",
        kind: "diagnostic",
      },
    ],
  },
];


export const shellRoutes: readonly ShellRoute[] = navigationGroups.flatMap(
  (group) => group.routes,
);

export function findShellRoute(path: string): ShellRoute | undefined {
  return shellRoutes.find((route) => route.path === path);
}

export function minimumRoleForRoute(route: Pick<ShellRoute, "kind">): MinimumRouteRole {
  if (
    route.kind === "data-upload"
    || route.kind === "amazon-data-upload"
    || route.kind === "joybuy-data-upload"
    || route.kind === "data-sources"
    || route.kind === "master-data"
    || route.kind === "users-access"
  ) return "ADMIN";
  return "VIEWER";
}
